import React from 'react'
import styled from '@emotion/styled'
import { type InfiniteData, useInfiniteQuery } from '@tanstack/react-query'
import { radius, spacing } from '@mobi/component-library/Foundations'
import { LoadingPlaceholder } from '@mobi/component-library/Misc/LoadingPlaceholder'
import type { Tabs, TicketResponseData } from '@core/Areas/AccountActivity/types'
import { setShouldAllowPendingRacingTicketCountPolling } from '@core/Areas/AppHeader/Store'
import { AutoFetchButton } from '@core/Components/AutoFetchButton'
import type { PendingTicketsCountResponse } from '@core/Data/Account/PendingTicketsCount/helpers/api'
import { queryClient } from '@core/Data/ReactQuery'
import { queryKeys } from '@core/Data/ReactQuery/constants'
import { trackInfinityScrollEvent } from '@core/Services/Analytics/helpers/trackers'
import { store } from '@core/Store'
import { fetchPendingTicketsData } from '../helpers/api'

export const useAccountActivityData = ({
  activeTab,
  isOrderByEventStart,
  selectedDate,
}: {
  activeTab: Tabs
  isOrderByEventStart: boolean
  selectedDate?: string
}) => {
  const queryKey = queryKeys.accountActivityData(activeTab, isOrderByEventStart, selectedDate)

  const { fetchNextPage, isFetchingNextPage, isLoading, isError, data, refetch, isSuccess } =
    useInfiniteQuery({
      queryKey,
      queryFn: ({ pageParam: nextPage }) =>
        fetchPendingTicketsData({ nextPage, activeTab, isOrderByEventStart, selectedDate }),
      initialPageParam: '',
      getNextPageParam: prevPage => {
        if (prevPage.isFinalPage) return undefined
        return prevPage._nextPage || null
      },
      staleTime: 1_000 * 60 * 2, // 2 mins
      // Ensure fresh data on entry, rather than invalidate in all locations where bets are placed
      refetchOnMount: 'always',
    })

  React.useEffect(() => {
    if (isSuccess) {
      if (activeTab === 'pending') {
        const hasPendingTickets = data.pages[0].sortedIds.length > 0
        store.dispatch(setShouldAllowPendingRacingTicketCountPolling(hasPendingTickets))

        if (data.pages.at(-1)?.isFinalPage) {
          const count = data.pages.reduce((count, page) => page.sortedIds.length + count, 0)
          patchRacingPendingBetsCountQueryData(count)
        }
      }

      handleInfinityScrollTracking(data, activeTab)
    }
  }, [activeTab, data, isSuccess])

  const isFinalPage = data && data.pages.length > 0 && data.pages[data.pages.length - 1].isFinalPage
  const isEmptyState = isFinalPage && data.pages[0].sortedIds.length === 0

  return {
    data,
    isError,
    isLoading,
    isEmptyState,
    isFinalPage,
    refetch: () => {
      queryClient.removeQueries({ queryKey })
      refetch()
    },
    /** Will auto fetch next page when in view */
    LoadMoreButton: () => (
      <AutoFetchButton onClick={fetchNextPage} isFetching={isFetchingNextPage} />
    ),
    SkeletonLoader,
  }
}

// ===============
// Local Component
// ===============

const SkeletonLoader: React.FC = () => (
  <WrapperStyled>
    {Array(9)
      .fill(null)
      .map((_, i) => (
        <LoadingPlaceholder key={i} width='100%' height='10rem' borderRadius={radius.lg} />
      ))}
  </WrapperStyled>
)

// =============
// Local Helpers
// =============

function patchRacingPendingBetsCountQueryData(pendingTicketsCount: number) {
  queryClient.setQueryData<PendingTicketsCountResponse>(
    queryKeys.pendingTicketsCountRacing,
    currentData => {
      if (!currentData) return currentData
      if (currentData.pendingTicketsCount === pendingTicketsCount) return currentData
      const newData: PendingTicketsCountResponse = { pendingTicketsCount }
      return newData
    }
  )
}

function handleInfinityScrollTracking(data: InfiniteData<TicketResponseData, unknown>, tab: Tabs) {
  const location = locationNames[tab]
  trackInfinityScrollEvent({ location, loadCount: data.pages.length })
}

const locationNames: Record<Tabs, Parameters<typeof trackInfinityScrollEvent>[0]['location']> = {
  pending: 'Pending Bets',
  results: 'Resulted Bets',
  transactions: 'Debit / Credit',
  rebet: 'Re-Bet',
}

// ======
// Styles
// ======

const WrapperStyled = styled.div({
  position: 'absolute',
  top: 0,
  bottom: 0,
  width: '100%',
  display: 'flex',
  flexDirection: 'column',
  gap: spacing.smx1,
})
